export interface PageModule {
  render(params?: Record<string, string>): string;
  mount(params?: Record<string, string>): void;
  unmount(): void;
}

// Per-page tribal class applied to <body>
const pageTribes: Record<string, string> = {
  '/': 'tribe-lakota',
  '/about': 'tribe-aztec',
  '/trading-lab': 'tribe-maya',
  '/dashboard': 'tribe-navajo',
  '/strategies': 'tribe-apache',
  '/backtests': 'tribe-hopi',
  '/risk-engine': 'tribe-cherokee',
  '/dev-center': 'tribe-inca',
  '/docs': 'tribe-olmec',
  '/releases': 'tribe-haida',
  '/store': 'tribe-comanche',
  '/blog': 'tribe-pueblo',
  '/contact': 'tribe-ojibwe',
};

function setTribeClass(path: string): void {
  const body = document.body;
  body.classList.forEach(cls => { if (cls.startsWith('tribe-')) body.classList.remove(cls); });
  const tribe = pageTribes[path] ?? 'tribe-lakota';
  body.classList.add(tribe);
}

// Lazy-loaded page registry
const pageLoaders: Record<string, () => Promise<PageModule>> = {
  '/': () => import('./pages/home.js'),
  '/about': () => import('./pages/about.js'),
  '/trading-lab': () => import('./pages/trading-lab.js'),
  '/dashboard': () => import('./pages/dashboard.js'),
  '/strategies': () => import('./pages/strategies.js'),
  '/backtests': () => import('./pages/backtests.js'),
  '/risk-engine': () => import('./pages/risk-engine.js'),
  '/dev-center': () => import('./pages/dev-center.js'),
  '/docs': () => import('./pages/docs.js'),
  '/releases': () => import('./pages/releases.js'),
  '/store': () => import('./pages/store.js'),
  '/blog': () => import('./pages/blog.js'),
  '/contact': () => import('./pages/contact.js'),
};

let currentPage: PageModule | null = null;

function getRoute(): { path: string; params: Record<string, string> } {
  const raw = window.location.hash.replace(/^#/, '') || '/';
  // Simple param extraction: /strategies/momentum-merchant -> /strategies/:slug
  const parts = raw.split('/').filter(Boolean);

  if (parts.length >= 2 && parts[0] === 'strategies') {
    return { path: '/strategies', params: { slug: parts[1] ?? '' } };
  }

  return { path: raw || '/', params: {} };
}

async function navigate(): Promise<void> {
  const app = document.getElementById('app');
  if (!app) return;

  const { path, params } = getRoute();

  // Unmount current page
  if (currentPage) {
    try {
      currentPage.unmount();
    } catch (e) {
      console.warn('[router] unmount error:', e);
    }
    currentPage = null;
  }

  // Set tribal body class
  setTribeClass(path);

  // Show loading
  app.innerHTML = '<div class="loading-state">Loading...</div>';
  window.scrollTo(0, 0);

  // Load page
  const loader = pageLoaders[path] ?? pageLoaders['/'];
  try {
    const page = await loader!();
    currentPage = page;

    app.innerHTML = page.render(params);
    page.mount(params);
  } catch (err) {
    console.error('[router] page load error:', err);
    app.innerHTML = `
      <div style="padding:4rem 2rem;text-align:center;">
        <h2 style="color:var(--color-text-danger);margin-bottom:1rem;">404 — Page Not Found</h2>
        <p>The route <code>${path}</code> could not be loaded.</p>
        <button onclick="window.location.hash=''" class="btn btn-secondary" style="margin-top:1rem;">Go Home</button>
      </div>
    `;
  }
}

export function initRouter(): void {
  window.addEventListener('hashchange', () => {
    void navigate();
  });
  void navigate();
}

export function navigateTo(path: string): void {
  window.location.hash = path;
}
